import { Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SlideUp, StaggerContainer, StaggerItem } from "@/components/ui/motion-wrappers";
import { cn } from "@/lib/utils";

interface FeaturesGridProps {
    title: string;
    subtitle: string;
    features: string[];
    theme?: "teal" | "purple" | "indigo" | "amber" | "slate" | "blue" | "default";
    className?: string;
}

const themeStyles = {
    teal: {
        iconBg: "bg-teal-100 dark:bg-teal-900/30",
        iconColor: "text-teal-600 dark:text-teal-400",
        hoverBorder: "hover:border-teal-500/50"
    },
    purple: {
        iconBg: "bg-purple-100 dark:bg-purple-900/30",
        iconColor: "text-purple-600 dark:text-purple-400",
        hoverBorder: "hover:border-purple-500/50"
    },
    indigo: {
        iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
        iconColor: "text-indigo-600 dark:text-indigo-400",
        hoverBorder: "hover:border-indigo-500/50"
    },
    amber: {
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        hoverBorder: "hover:border-amber-500/50"
    },
    slate: {
        iconBg: "bg-slate-100 dark:bg-slate-800",
        iconColor: "text-slate-900 dark:text-white",
        hoverBorder: "hover:border-slate-500/50"
    },
    blue: {
        iconBg: "bg-[#2F5FE8]/10",
        iconColor: "text-[#2F5FE8]",
        hoverBorder: "hover:border-[#2F5FE8]/50"
    },
    default: {
        iconBg: "bg-primary/10",
        iconColor: "text-primary",
        hoverBorder: "hover:border-primary/50"
    }
};

export const FeaturesGrid = ({
    title,
    subtitle,
    features,
    theme = "default",
    className
}: FeaturesGridProps) => {
    const styles = themeStyles[theme];

    return (
        <section className={cn("py-24 bg-background", className)}>
            <div className="container mx-auto px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <SlideUp>
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                {title}
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                {subtitle}
                            </p>
                        </SlideUp>
                    </div>

                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <StaggerItem key={index}>
                                <Card className={cn("h-full border-2 transition-colors", styles.hoverBorder)}>
                                    <CardContent className="pt-6">
                                        <div className="flex items-start gap-3">
                                            <div className={cn("mt-1 h-8 w-8 rounded-full flex items-center justify-center shrink-0", styles.iconBg)}>
                                                <Check className={cn("h-4 w-4", styles.iconColor)} />
                                            </div>
                                            <span className="font-medium text-lg">{feature}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </div>
        </section>
    );
};
